import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Card } from '@/hooks/useCards';

export type PinnedCard = {
  id: string;
  user_id: string;
  card_id: string;
  display_order: number;
  created_at: string;
  card?: Card;
};

export function usePinnedCards(userId?: string) {
  const { user } = useAuth();
  const targetId = userId || user?.id;
  return useQuery({
    queryKey: ['pinned-cards', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      const { data, error } = await supabase
        .from('user_pinned_cards')
        .select('*, card:cards(*)')
        .eq('user_id', targetId)
        .order('display_order', { ascending: true })
        .limit(6);
      if (error) throw error;
      return (data || []) as PinnedCard[];
    },
    enabled: !!targetId,
  });
}

export function usePinCard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cardId, order }: { cardId: string; order: number }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('user_pinned_cards').insert({
        user_id: user.id,
        card_id: cardId,
        display_order: order,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pinned-cards'] }),
  });
}

export function useUnpinCard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cardId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('user_pinned_cards').delete().eq('user_id', user.id).eq('card_id', cardId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pinned-cards'] }),
  });
}
